import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const AdminRolesPanel = () => {
  const [activeSubTab, setActiveSubTab] = useState('roles');

  const subTabs = [
    { id: 'roles', label: 'Roles', icon: 'Shield' },
    { id: 'permissions', label: 'Permissions', icon: 'Lock' },
    { id: 'assignments', label: 'Assignments', icon: 'Users' }
  ];

  const mockRoles = [
    { id: 1, name: 'Super Admin', users: 2, permissions: 'All', description: 'Full system access', active: true },
    { id: 2, name: 'Content Manager', users: 3, permissions: 'Content', description: 'Manage content and moderation', active: true },
    { id: 3, name: 'Vendor Manager', users: 2, permissions: 'Vendors', description: 'Manage vendors and listings', active: true },
    { id: 4, name: 'Support Agent', users: 4, permissions: 'Support', description: 'Handle customer support', active: true }
  ];

  const mockAdmins = [
    { id: 1, name: 'John Doe', email: 'john@shaadinest.com', role: 'Super Admin', lastLogin: '2024-01-15', active: true },
    { id: 2, name: 'Jane Smith', email: 'jane@shaadinest.com', role: 'Content Manager', lastLogin: '2024-01-14', active: true },
    { id: 3, name: 'Mike Johnson', email: 'mike@shaadinest.com', role: 'Vendor Manager', lastLogin: '2024-01-13', active: true }
  ];

  const renderSubTabContent = () => {
    switch (activeSubTab) {
      case 'roles':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-soft p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
                <h3 className="font-heading font-semibold text-lg text-text-primary">
                  Admin Roles
                </h3>
                <Button variant="primary" iconName="Plus" iconSize={16}>
                  Add Role
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {mockRoles.map((role) => (
                  <div key={role.id} className="border border-border rounded-xl p-6 hover:border-primary-200 transition-colors">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-heading font-semibold text-lg text-text-primary">
                        {role.name}
                      </h4>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        role.active ? 'bg-success-100 text-success' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {role.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    
                    <p className="text-text-secondary text-sm mb-4">
                      {role.description}
                    </p>
                    
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-4">
                        <div className="text-sm">
                          <span className="text-text-secondary">Users: </span>
                          <span className="font-medium text-text-primary">{role.users}</span>
                        </div>
                        <div className="text-sm">
                          <span className="text-text-secondary">Permissions: </span>
                          <span className="font-medium text-text-primary">{role.permissions}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm" className="flex-1">
                        Edit
                      </Button>
                      <Button variant="ghost" size="sm">
                        <Icon name="MoreVertical" size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case 'permissions':
        return (
          <div className="bg-white rounded-xl shadow-soft p-6">
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Lock" size={32} className="text-primary" />
              </div>
              <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
                Permission Management
              </h3>
              <p className="text-text-secondary mb-6">
                Configure role-based permissions and access control
              </p>
              <Button variant="outline" disabled>
                Manage Permissions
              </Button>
            </div>
          </div>
        );
      case 'assignments':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-soft p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
                <h3 className="font-heading font-semibold text-lg text-text-primary">
                  Role Assignments
                </h3>
                <Button variant="primary" iconName="Plus" iconSize={16}>
                  Add Admin
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <Input placeholder="Search admins..." icon="Search" />
                <select className="px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option>All Roles</option>
                  <option>Super Admin</option>
                  <option>Content Manager</option>
                  <option>Vendor Manager</option>
                  <option>Support Agent</option>
                </select>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-surface-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                        Admin
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                        Role
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                        Last Login
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {mockAdmins.map((admin) => (
                      <tr key={admin.id} className="hover:bg-surface-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                              <Icon name="User" size={16} className="text-primary" />
                            </div>
                            <div>
                              <span className="font-medium text-text-primary">{admin.name}</span>
                              <div className="text-xs text-text-secondary">{admin.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary">
                            {admin.role}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-text-secondary">{admin.lastLogin}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            admin.active ? 'bg-success-100 text-success' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {admin.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-2">
                            <button className="text-primary hover:text-primary-600">
                              <Icon name="Edit" size={16} />
                            </button>
                            <button className="text-red-500 hover:text-red-700">
                              <Icon name="UserX" size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-primary to-secondary rounded-xl p-6 text-white">
        <h2 className="font-heading font-bold text-2xl mb-2">
          Admin Roles
        </h2>
        <p className="text-white/90">
          Manage admin roles and permissions
        </p>
      </div>

      {/* Sub Navigation */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <div className="flex flex-wrap gap-2">
          {subTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                activeSubTab === tab.id
                  ? 'bg-primary text-white' :'text-text-secondary hover:bg-surface-100'
              }`}
            >
              <Icon name={tab.icon} size={16} />
              <span className="font-medium">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Sub Tab Content */}
      {renderSubTabContent()}
    </div>
  );
};

export default AdminRolesPanel;